import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { UserRole } from '@cwr/shared';
import apiClient from '@/api/client';

export interface AuthUser {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  orgId: number;
  orgName: string;
  orgSlug: string;
  orgTier: string;
  mfaEnabledAt: string | null;
  verifiedAt: string | null;
}

interface LoginResponse {
  token: string;
  user: AuthUser;
  mfaRequired: boolean;
  mfaSessionToken: string | null;
}

interface MfaLoginResponse {
  token: string;
  user: AuthUser;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const mfaSessionToken = ref<string | null>(null);
  const mfaPendingUser = ref<AuthUser | null>(null);

  const isAuthenticated = computed(() => !!user.value);
  const userRole = computed(() => user.value?.role ?? null);

  async function login(email: string, password: string): Promise<'success' | 'mfa_required'> {
    loading.value = true;
    error.value = null;

    try {
      const response = await apiClient.post<LoginResponse>('/auth/login', { email, password });
      const data = response.data;

      if (data.mfaRequired) {
        mfaSessionToken.value = data.mfaSessionToken;
        mfaPendingUser.value = data.user;
        return 'mfa_required';
      }

      // The backend sets the httpOnly cwr_auth cookie — nothing to store locally.
      user.value = data.user;
      return 'success';
    } catch (err: unknown) {
      error.value = extractErrorMessage(err, 'Login failed');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function completeMfaLogin(totpCode: string): Promise<void> {
    if (!mfaSessionToken.value) {
      throw new Error('No MFA session active');
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await apiClient.post<MfaLoginResponse>('/auth/login/mfa', {
        mfaSessionToken: mfaSessionToken.value,
        totpCode,
      });

      user.value = response.data.user;
      mfaSessionToken.value = null;
      mfaPendingUser.value = null;
    } catch (err: unknown) {
      error.value = extractErrorMessage(err, 'MFA verification failed');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchMe(): Promise<void> {
    try {
      const response = await apiClient.get<AuthUser>('/auth/me');
      user.value = response.data;
    } catch {
      // Cookie missing/expired or user deactivated — clear local state silently.
      user.value = null;
    }
  }

  async function logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Logout endpoint failed — still clear local state. The cookie will
      // either have been cleared server-side or will expire on its own.
    } finally {
      user.value = null;
      mfaSessionToken.value = null;
      mfaPendingUser.value = null;
    }
  }

  async function register(email: string, name: string, password: string): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      await apiClient.post('/auth/register', { email, name, password });
    } catch (err: unknown) {
      error.value = extractErrorMessage(err, 'Registration failed');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function forgotPassword(email: string): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      await apiClient.post('/auth/forgot-password', { email });
    } catch (err: unknown) {
      error.value = extractErrorMessage(err, 'Request failed');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function resetPassword(token: string, password: string): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      await apiClient.post('/auth/reset-password', { token, password });
    } catch (err: unknown) {
      error.value = extractErrorMessage(err, 'Password reset failed');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function verifyEmail(token: string): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      await apiClient.post('/auth/verify-email', { token });
    } catch (err: unknown) {
      error.value = extractErrorMessage(err, 'Email verification failed');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function setupMfa(): Promise<{ qrCodeUrl: string; secret: string }> {
    const response = await apiClient.post<{ qrCodeUrl: string; secret: string }>('/auth/mfa/setup');
    return response.data;
  }

  async function verifyMfaSetup(totpCode: string): Promise<{ backupCodes: string[] }> {
    const response = await apiClient.post<{ backupCodes: string[] }>('/auth/mfa/verify', { totpCode });
    // Refresh user data to update mfaEnabledAt
    await fetchMe();
    return response.data;
  }

  async function lockdown(): Promise<void> {
    await apiClient.post('/auth/lockdown');
    user.value = null;
  }

  function clearError() {
    error.value = null;
  }

  return {
    user,
    loading,
    error,
    mfaSessionToken,
    mfaPendingUser,
    isAuthenticated,
    userRole,
    login,
    completeMfaLogin,
    fetchMe,
    logout,
    register,
    forgotPassword,
    resetPassword,
    verifyEmail,
    setupMfa,
    verifyMfaSetup,
    lockdown,
    clearError,
  };
});

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { message?: string } } }).response;
    if (response?.data?.message) {
      return Array.isArray(response.data.message)
        ? response.data.message[0]
        : response.data.message;
    }
  }
  if (err instanceof Error) {
    return err.message;
  }
  return fallback;
}
